/**
 * Who is playing: an ensemble to start from, the players it seats, and one more or one fewer
 * of each.
 *
 * A roster is mostly edited, not built: the reader picks the orchestra nearest to what they
 * want and then takes three players out or puts two more in. So every chip carries both a
 * minus and a plus, and the whole roster can be cleared in one click; a preset is a place to
 * start, not a door that closes behind the reader once they have changed one seat of it.
 *
 * A piano is one pianist on a grand staff, alone (its own preset) or beside any players, where
 * it plays what they were not seated for, and a harp, an organ and a celesta are one player
 * each the same way. None is one of the generator's seats, so they are offered beside them
 * (`HANDED`); a roster may have more than one, each on a grand staff of its own.
 */

import { ENSEMBLES, HANDED, handedIn, type Seat } from "@app/shared-types";
import { oneFewer, oneMore, sameSeats, tally } from "../lib/roster";
import { rosterNotices } from "../lib/roster-notice";
import { SELECT } from "./Chrome";
import InstrumentPicker, { fromSeats, type Pick } from "./InstrumentPicker";

/**
 * What this roster will silence (`lib/roster-notice`), weighed as the generator weighs it:
 * players of two hands alone are not a roster of players and silence nothing said there, and
 * one beside players is its two staves, of its own family. It is given `book` so that it is
 * not named among the instruments outside the book, which the generator does not name it among
 * either: what it says of two hands it says in the result.
 */
export function silencedBy(roster: readonly string[], seats: readonly Seat[]): string[] {
  const two = handedIn(roster);
  if (two.length === 0) return rosterNotices(roster, seats);
  const hands = roster.filter((instrument) => two.some((one) => one.instrument === instrument));
  if (hands.length === roster.length) return [];
  const staves = two.map((one) => ({ ...one, book: true, voiced: false }));
  return rosterNotices([...roster, ...hands], [...seats, ...staves]);
}

/** Every player a roster can be given: the players of two hands, then the generator's seats. */
export function playerPicks(seats: readonly Seat[]): Pick[] {
  return [
    ...HANDED.map((one) => ({ value: one.instrument, label: one.label })),
    ...fromSeats(seats),
  ];
}

/** What an instrument is called on its chip: the pipeline's label for its seat. */
export function seatLabel(instrument: string, seats: readonly Seat[]): string {
  return (
    HANDED.find((one) => one.instrument === instrument)?.label ??
    seats.find((one) => one.instrument === instrument)?.label ??
    instrument
  );
}

/**
 * The preset this roster is, if it is one, so the menu can show it chosen.
 *
 * An empty roster is not a preset and not a custom one either: it is the question unanswered,
 * and the menu says so rather than naming an ensemble nobody picked.
 */
export function presetOf(roster: readonly string[]): string {
  return ENSEMBLES.find((one) => sameSeats(one.roster, roster))?.name ?? "";
}

const KEY = "h-5 w-5 rounded text-shell-dim hover:bg-shell-hot hover:text-shell-lit";

export interface ArrangeRosterProps {
  readonly roster: readonly string[];
  readonly seats: readonly Seat[];
  onRoster(roster: string[]): void;
}

export default function ArrangeRoster({
  roster,
  seats,
  onRoster,
}: ArrangeRosterProps): JSX.Element {
  const preset = presetOf(roster);
  const labelFor = (instrument: string): string => seatLabel(instrument, seats);

  return (
    <>
      <select
        value={preset}
        onChange={(event) => {
          const found = ENSEMBLES.find((one) => one.name === event.target.value);
          if (found !== undefined) onRoster([...found.roster]);
        }}
        aria-label="Ensemble"
        className={SELECT}
      >
        {preset === "" ? (
          <option value="">{roster.length === 0 ? "Who is playing?" : "Custom roster"}</option>
        ) : null}
        {ENSEMBLES.map((one) => (
          <option key={one.name} value={one.name}>
            {one.name}
          </option>
        ))}
      </select>

      <div className="flex flex-wrap items-center gap-1">
        {[...tally(roster)].map(([instrument, many]) => (
          <span
            key={instrument}
            className="inline-flex h-7 items-center gap-0.5 rounded bg-shell-key pl-2 pr-1 text-[11px] text-shell-lit"
          >
            <span className="mr-0.5">{labelFor(instrument)}</span>
            {many > 1 ? <span className="font-mono text-shell-dim">×{many}</span> : null}
            <button
              type="button"
              aria-label={`Remove one ${labelFor(instrument)}`}
              title="One fewer"
              onClick={() => onRoster(oneFewer(roster, instrument))}
              className={KEY}
            >
              −
            </button>
            <button
              type="button"
              aria-label={`Add one ${labelFor(instrument)}`}
              title="One more"
              onClick={() => onRoster(oneMore(roster, instrument))}
              className={KEY}
            >
              +
            </button>
          </span>
        ))}
        {seats.length === 0 ? null : (
          <InstrumentPicker
            look="shell"
            label="Add a player"
            placeholder="+ Player"
            value={null}
            options={playerPicks(seats)}
            onPick={(instrument) => onRoster([...roster, instrument])}
            title="Where there is a player for each staff of the original and no piano, harp, organ or celesta, each staff goes whole to one player. Otherwise each player is given one line of it at a time: the tune, the bass, or notes between them, and notes sounding together beyond the players there are are left out and counted. A player the music gives nothing to rests, unless Double the lines is ticked, Harmony is set beyond As written, or a Counter is chosen. A piano, a harp, an organ or a celesta beside players plays, on a grand staff under them, the inner notes no player was free for and the bass where no player has it; two of them share it, the lowest keeping the bass and the notes between dealt them by turns."
            className="w-32"
          />
        )}
        {roster.length === 0 ? null : (
          <button
            type="button"
            onClick={() => onRoster([])}
            title="Take every player off, to build a roster from nothing."
            className="h-7 rounded px-2 text-[11px] text-shell-dim hover:bg-shell-hot hover:text-shell-lit"
          >
            Clear
          </button>
        )}
      </div>
    </>
  );
}
